import { useSelector } from 'react-redux';

import { FEATURE_FLAGS } from '@constants/feature';

export type CurrencyConfig = {
  code: string;
  locale: string;
  symbol: string;
};

// Default currency configuration (Indian Rupee)
export const DEFAULT_CURRENCY_CONFIG: CurrencyConfig = {
  code: 'INR',
  locale: 'en-IN',
  symbol: '₹'
};

// Converts locale format from underscore to hyphen (e.g., "en_IN" -> "en-IN")
const normalizeLocale = (locale: string): string => locale.replace(/_/g, '-');

/**
 * Custom hook for currency formatting
 * Provides formatting functions based on currency config from Redux store
 * Falls back to INR if no currency config is set
 */
const useCurrency = () => {
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const { currencyConfig: reduxCurrencyConfig } = useSelector(
    (state: any) => state.user
  );

  const isMultiCurrencySupportEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.MULTI_CURRENCY_SUPPORT
  );

  const getCurrencyConfig = (): CurrencyConfig => {
    if (!isMultiCurrencySupportEnabled) {
      return DEFAULT_CURRENCY_CONFIG;
    }

    if (!reduxCurrencyConfig || reduxCurrencyConfig.code === 'INR') {
      return DEFAULT_CURRENCY_CONFIG;
    }

    return {
      code: reduxCurrencyConfig.code,
      locale: normalizeLocale(reduxCurrencyConfig.locale).replace('_', '-'),
      symbol: reduxCurrencyConfig.symbol
    };
  };

  const currencyConfig: CurrencyConfig = getCurrencyConfig();

  /**
   * Formats amount and returns symbol and value separately
   * Useful when symbol needs different styling than the value
   *
   * @example
   * formatCurrencyValue(1234.56)
   * // Returns: { symbol: '₹', value: '1,234.56' }
   *
   * formatCurrencyValue(1000.00)
   * // Returns: { symbol: '₹', value: '1,000' } (removes trailing .00)
   *
   * formatCurrencyValue(null)
   * // Returns: { symbol: '₹', value: '0' }
   */
  const formatCurrencyValue = (
    amount: number | null | undefined
  ): { symbol: string; value: string } => {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) {
      return { symbol: currencyConfig.symbol, value: '0' };
    }

    const formatter = new Intl.NumberFormat(currencyConfig.locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    const formattedValue = formatter.format(amount);
    const cleanValue = formattedValue.replace(/\.00$/, '');

    return {
      symbol: currencyConfig.symbol,
      value: cleanValue
    };
  };

  /**
   * Formats amount in compact notation WITHOUT the currency symbol
   * Useful when symbol needs to be displayed separately with different styling
   *
   * @example
   * formatCompactValue(1500)
   * // Returns: '1.5K'
   *
   * formatCompactValue(1200000)
   * // Returns: '12L' (for en-IN locale)
   *
   * formatCompactValue(2500000)
   * // Returns: '25L' (for en-IN locale)
   *
   * formatCompactValue(null)
   * // Returns: '0'
   */
  const formatCompactValue = (amount: number): string => {
    if (amount === null || amount === undefined) return '0';

    const formatter = new Intl.NumberFormat(currencyConfig.locale, {
      style: 'decimal',
      notation: 'compact',
      compactDisplay: 'short'
    });

    return formatter.format(amount);
  };

  /**
   * Formats amount in compact notation WITH the currency symbol
   * Returns complete formatted string with symbol prefixed
   *
   * @example
   * formatCompactCurrency(1500)
   * // Returns: '₹1.5K'
   *
   * formatCompactCurrency(1200000)
   * // Returns: '₹12L' (for en-IN locale)
   *
   * formatCompactCurrency(null)
   * // Returns: '₹0'
   */
  const formatCompactCurrency = (amount: number): string => {
    if (amount === null || amount === undefined)
      return `${currencyConfig.symbol}0`;

    if (currencyConfig.symbol) {
      return `${currencyConfig.symbol}${formatCompactValue(amount)}`;
    }

    const formatter = new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyConfig.code,
      notation: 'compact',
      compactDisplay: 'short',
      currencyDisplay: 'narrowSymbol'
    });

    return formatter.format(amount);
  };

  /**
   * Formats amount in full notation WITH the currency symbol
   * Returns complete formatted string with symbol prefixed
   *
   * @example
   * formatCurrencyFull(1234.56)
   * // Returns: '₹1,234.56'
   *
   * formatCurrencyFull(1000000)
   * // Returns: '₹10,00,000' (for en-IN locale)
   *
   * formatCurrencyFull(null)
   * // Returns: '₹0'
   */
  const formatCurrencyFull = (amount: number): string => {
    if (amount === null || amount === undefined)
      return `${currencyConfig.symbol}0`;

    if (currencyConfig.symbol) {
      const formatter = new Intl.NumberFormat(currencyConfig.locale, {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      return `${currencyConfig.symbol}${formatter.format(amount)}`;
    }

    const formatter = new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyConfig.code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    return formatter.format(amount);
  };

  return {
    currencyConfig,
    formatCurrencyValue,
    formatCompactValue,
    formatCompactCurrency,
    formatCurrencyFull
  };
};

export default useCurrency;
